import { useState, useEffect } from 'react';
import { ClipboardList, TrendingDown, TrendingUp, Minus, Trash2, Calendar } from 'lucide-react';
import { SectionHeader } from './SectionHeader';
import { HistoryEntry } from '@/types/fitness';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface HistoryTableProps {
  historial: HistoryEntry[];
  onDeleted?: () => void;
}

function isDeletable(entry: HistoryEntry): boolean {
  if (entry.sesion?.toLowerCase().includes('coach')) return false;
  if (!entry.fecha || entry.fecha.split('/').length !== 3) return false;
  return true;
}

async function deleteMeasurement(userId: string, entry: HistoryEntry): Promise<boolean> {
  try {
    const parts = entry.fecha?.split('/');
    if (!parts || parts.length !== 3) return false;
    const { error } = await supabase
      .from('body_measurements')
      .delete()
      .eq('user_id', userId)
      .eq('measure_date', `${parts[2]}-${parts[1]}-${parts[0]}`)
      .eq('session_label', entry.sesion);
    return !error;
  } catch { return false; }
}

function Diff({ value, lowerIsBetter = true }: { value: number; lowerIsBetter?: boolean }) {
  if (value === 0) return <span className="text-[10px] text-muted-foreground">—</span>;
  const isGood = lowerIsBetter ? value < 0 : value > 0;
  const Icon = isGood ? TrendingDown : TrendingUp;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${isGood ? 'text-green-500' : 'text-red-400'}`}>
      <Icon className="w-3 h-3" />
      {value > 0 ? '+' : ''}{value.toFixed(1)}
    </span>
  );
}

const HEADERS = ['Sesión', 'Fecha', 'Peso kg', 'IMC', 'Grasa %', 'M. Libre kg', 'Agua L', 'G. Visceral', ''];

export function HistoryTable({ historial, onDeleted }: HistoryTableProps) {
  const { user } = useAuth();
  const [deleting, setDeleting] = useState<string | null>(null);
  const [localHistorial, setLocalHistorial] = useState<HistoryEntry[]>(historial);

  // Sync local state when parent re-fetches
  useEffect(() => { setLocalHistorial(historial); }, [historial]);

  const sorted = [...localHistorial].sort((a, b) => {
    const parse = (s: string) => {
      const p = s.split('/');
      return p.length === 3 ? new Date(`${p[2]}-${p[1]}-${p[0]}`).getTime() : 0;
    };
    return parse(a.fecha) - parse(b.fecha);
  });

  const first = sorted[0];

  const displayLabel = (i: number) => i === 0 ? 'Baseline' : `Sesión ${i}`;

  const handleDelete = async (entry: HistoryEntry) => {
    if (!user) return;
    setDeleting(entry.sesion);
    // Optimistic update — remove immediately from UI
    setLocalHistorial(prev => prev.filter(e => e !== entry));
    const ok = await deleteMeasurement(user.id, entry);
    if (!ok) {
      // Revert on failure
      setLocalHistorial(historial);
      toast.error('Error al eliminar');
    } else {
      toast.success('Medición eliminada');
      onDeleted?.();
    }
    setDeleting(null);
  };

  const DeleteButton = ({ entry, label }: { entry: HistoryEntry; label: string }) => (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all" title="Eliminar">
          <Trash2 size={13} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Eliminar esta medición?</AlertDialogTitle>
          <AlertDialogDescription>
            Eliminarás <strong>{label}</strong> del {entry.fecha}. Esta acción no se puede deshacer.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => handleDelete(entry)} className="bg-destructive hover:bg-destructive/90" disabled={deleting === entry.sesion}>
            {deleting === entry.sesion ? 'Eliminando...' : 'Sí, eliminar'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <section id="historial" className="scroll-mt-32">
      <SectionHeader title="Historial" subtitle="Mediciones Bioimpedancia" icon={ClipboardList} />

      {/* ── DESKTOP (md+) ── */}
      <div className="hidden md:block rounded-2xl overflow-hidden border border-border shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gradient-to-r from-primary to-primary/80">
                {HEADERS.map((h, i) => (
                  <th key={i} className={`px-4 py-3 text-[10px] font-black uppercase tracking-widest text-accent whitespace-nowrap ${i >= 2 && i < 8 ? 'text-right' : ''}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((entry, i) => {
                const isBaseline = i === 0;
                const isEven = i % 2 === 0;
                const pesoDiff = !isBaseline ? entry.peso - first.peso : 0;
                const grasaDiff = !isBaseline && entry.grasa && first.grasa ? entry.grasa - first.grasa : 0;
                const showDelete = user && i > 0 && sorted.length > 1 && isDeletable(entry);
                return (
                  <tr key={i} className={`border-b border-border/50 transition-colors hover:bg-accent/5 ${isBaseline ? 'bg-accent/10' : isEven ? 'bg-card' : 'bg-secondary/20'}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-xs uppercase tracking-wide">{displayLabel(i)}</span>
                        {isBaseline && (
                          <span className="text-[9px] font-bold bg-accent/20 text-accent px-1.5 py-0.5 rounded-full uppercase tracking-wide">Base</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground font-medium whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3 h-3" />
                        {entry.fecha}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-black text-sm">{entry.peso}</div>
                      {!isBaseline && <Diff value={pesoDiff} />}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-sm">{entry.imc ?? '—'}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-black text-sm text-accent">{entry.grasa}%</div>
                      {!isBaseline && grasaDiff !== 0 && <Diff value={grasaDiff} />}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-sm">{entry.masaLibreGrasa ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-bold text-sm">{entry.agua ?? '—'}</td>
                    <td className="px-4 py-3 text-right font-bold text-sm">{entry.grasaVisceral ?? '—'}</td>
                    <td className="px-4 py-3 text-right">{showDelete && <DeleteButton entry={entry} label={displayLabel(i)} />}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── MOBILE (< md) ── */}
      <div className="md:hidden space-y-3">
        {sorted.map((entry, i) => {
          const isBaseline = i === 0;
          const pesoDiff = !isBaseline ? entry.peso - first.peso : 0;
          const grasaDiff = !isBaseline && entry.grasa && first.grasa ? entry.grasa - first.grasa : 0;
          const showDelete = user && i > 0 && sorted.length > 1 && isDeletable(entry);
          return (
            <div key={i} className={`rounded-2xl border overflow-hidden shadow-md ${isBaseline ? 'border-accent/40' : 'border-border'}`}>
              {/* Header strip */}
              <div className={`px-4 py-2.5 flex items-center justify-between ${isBaseline ? 'bg-accent/15' : 'bg-secondary/40'}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black uppercase tracking-wide">{displayLabel(i)}</span>
                  {isBaseline && <span className="text-[9px] font-bold bg-accent/25 text-accent px-2 py-0.5 rounded-full uppercase">Base</span>}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground font-medium">
                    <Calendar className="w-3 h-3" />
                    {entry.fecha}
                  </div>
                  {showDelete && <DeleteButton entry={entry} label={displayLabel(i)} />}
                </div>
              </div>

              {/* Metrics grid */}
              <div className="bg-card p-3 grid grid-cols-3 gap-2">
                {[
                  { label: 'Peso', value: `${entry.peso} kg`, diff: !isBaseline ? pesoDiff : null, accent: false },
                  { label: 'Grasa', value: `${entry.grasa}%`, diff: !isBaseline && grasaDiff !== 0 ? grasaDiff : null, accent: true },
                  { label: 'IMC', value: entry.imc ?? '—', diff: null, accent: false },
                  { label: 'M. Libre', value: entry.masaLibreGrasa ? `${entry.masaLibreGrasa} kg` : '—', diff: null, accent: false },
                  { label: 'Agua', value: entry.agua ? `${entry.agua} L` : '—', diff: null, accent: false },
                  { label: 'G. Visceral', value: entry.grasaVisceral ?? '—', diff: null, accent: false },
                ].map((m, j) => (
                  <div key={j} className="bg-secondary/30 rounded-xl p-2.5 flex flex-col gap-1">
                    <span className="text-[9px] font-black uppercase tracking-wider text-muted-foreground">{m.label}</span>
                    <span className={`text-sm font-black leading-none ${m.accent ? 'text-accent' : ''}`}>{m.value}</span>
                    {m.diff !== null && <Diff value={m.diff} />}
                  </div>
                ))}
              </div>

              {entry.metabolismoBasal && (
                <div className="bg-card px-3 pb-3">
                  <div className="bg-secondary/30 rounded-xl p-2.5">
                    <span className="text-[9px] font-black uppercase tracking-wider text-muted-foreground">Metabolismo Basal</span>
                    <p className="text-sm font-black mt-1">{entry.metabolismoBasal} kcal</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
